import db from "../db/database";
import { fetchFromBusinessCentralEspecial } from "../utils/businessCentralUtils";
import { saveCouponsToLocalDB } from "./couponService";
import { isOnline } from "./syncService";

// Función para sincronizar cupones desde Business Central
export const syncCoupons = async () => {
    if (!isOnline()) {
        console.log("Sin conexión a internet, usando cupones locales");
        return false;
    }

    try {
        console.log("Iniciando sincronización de cupones desde Business Central...");

        // Obtener cupones del servidor
        const response = await fetchFromBusinessCentralEspecial("Importaciones_getactivecoupons");
        const serverCoupons = JSON.parse(response).Active_Coupons;

        if (!serverCoupons || serverCoupons.length === 0) {
            console.log("No se encontraron cupones en el servidor");
            return false;
        }

        // Obtener cupones locales para comparar
        const localCoupons = await db.coupons.toArray();

        // Mapa para buscar cupones locales por No rápidamente
        const localCouponsByNo = {};
        localCoupons.forEach((coupon) => {
            if (coupon.No) {
                localCouponsByNo[coupon.No] = coupon;
            }
        });

        // Limpiar todas las tablas relacionadas con cupones antes de la sincronización
        await db.coupons.clear();
        await db.couponDetails.clear();
        await db.couponSalesPrices.clear();
        await db.couponLineDiscounts.clear();

        // Guardar todos los cupones del servidor
        await saveCouponsToLocalDB(serverCoupons);

        console.log(`Sincronización de cupones completada: ${serverCoupons.length} cupones sincronizados`);
        return true;

    } catch (error) {
        console.error("Error al sincronizar cupones:", error);
        return false;
    }
};

// Función para verificar si necesitamos sincronizar cupones
export const shouldSyncCoupons = async () => {
    try {
        const localCoupons = await db.coupons.toArray();

        // Si no hay cupones locales, necesitamos sincronizar
        if (localCoupons.length === 0) {
            return true;
        }

        // Verificar la última sincronización
        const lastSyncedCoupon = await db.coupons
            .orderBy("lastSynced")
            .reverse()
            .first();

        if (!lastSyncedCoupon || !lastSyncedCoupon.lastSynced) {
            return true;
        }

        // Si la última sincronización fue hace más de 1 hora, sincronizar
        const oneHourAgo = new Date(Date.now() - 60 * 60 * 1000);
        return lastSyncedCoupon.lastSynced < oneHourAgo;

    } catch (error) {
        console.error("Error al verificar si necesitamos sincronizar cupones:", error);
        return true; // En caso de error, mejor sincronizar
    }
};

// Función para obtener estadísticas de cupones locales
export const getCouponsStats = async () => {
    try {
        const allCoupons = await db.coupons.toArray();
        const activeCoupons = allCoupons.filter(c => c.Active && !c.deleted);
        const pendingCoupons = allCoupons.filter(c => c.Status === "Pendiente" && !c.deleted);
        const usedCoupons = allCoupons.filter(c => c.Status === "Utilizado" && !c.deleted);

        // Obtener detalles totales
        const totalDetails = await db.couponDetails.count();
        const totalSalesPrices = await db.couponSalesPrices.count();
        const totalLineDiscounts = await db.couponLineDiscounts.count();

        return {
            total: allCoupons.length,
            active: activeCoupons.length,
            pending: pendingCoupons.length,
            used: usedCoupons.length,
            details: totalDetails,
            salesPrices: totalSalesPrices,
            lineDiscounts: totalLineDiscounts,
            lastSync: allCoupons.length > 0 ?
                Math.max(...allCoupons.map(c => c.lastSynced?.getTime() || 0)) : null
        };
    } catch (error) {
        console.error("Error al obtener estadísticas de cupones:", error);
        return {
            total: 0,
            active: 0,
            pending: 0,
            used: 0,
            details: 0,
            salesPrices: 0,
            lineDiscounts: 0,
            lastSync: null
        };
    }
};

// Función para limpiar cupones locales (útil para testing o reset)
export const clearLocalCoupons = async () => {
    try {
        await db.coupons.clear();
        await db.couponDetails.clear();
        await db.couponSalesPrices.clear();
        await db.couponLineDiscounts.clear();

        console.log("Cupones locales limpiados correctamente");
        return true;
    } catch (error) {
        console.error("Error al limpiar cupones locales:", error);
        return false;
    }
};

// Función para forzar sincronización de cupones
export const forceSyncCoupons = async () => {
    console.log("Forzando sincronización de cupones...");
    return await syncCoupons();
}; 